import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../../../shared/services/supabase/client';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
const logger = createLogger('Update-password.jsx');

const stripRecoveryParamsFromUrl = () => {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        const next = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState(window.history.state, '', next);
    } catch {
        // ignore
    }
};

export default function UpdatePassword() {
    const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
    const [state, setState] = useState({ error: null, loading: false, message: null });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { updatePassword } = useAuth();
    const navigate = useNavigate();
    const sessionReadyRef = useRef(false);
    const codeExchangeAttemptedRef = useRef(false);

    // Recovery session can arrive after initial getSession (URL parsing / PASSWORD_RECOVERY event).
    // Also support PKCE links (?code=) via exchangeCodeForSession.
    useEffect(() => {
        let cancelled = false;

        const markReady = () => {
            if (cancelled || sessionReadyRef.current) {
                return;
            }
            sessionReadyRef.current = true;
            setState((prev) => ({
                ...prev,
                error: null,
                message: 'You can update your password below.',
                loading: false,
            }));
        };

        const markError = (message) => {
            if (cancelled || sessionReadyRef.current) {
                return;
            }
            setState((prev) => ({
                ...prev,
                error: message,
                loading: false,
                message: null,
            }));
        };

        const tryPkceCode = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            if (!code || codeExchangeAttemptedRef.current) {
                return false;
            }
            codeExchangeAttemptedRef.current = true;
            const { data, error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
                logger.warn('exchangeCodeForSession failed (link may use implicit flow instead)', {
                    message: error.message,
                });
                codeExchangeAttemptedRef.current = false;
                return false;
            }
            if (data?.session) {
                stripRecoveryParamsFromUrl();
                markReady();
                return true;
            }
            return false;
        };

        const tryGetSession = async () => {
            const {
                data: { session },
                error,
            } = await supabase.auth.getSession();
            if (error) {
                logger.error('Error checking session', error);
                return false;
            }
            if (session) {
                markReady();
                return true;
            }
            return false;
        };

        setState((prev) => ({ ...prev, loading: true, message: 'Checking authentication...', error: null }));

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            if (cancelled) {
                return;
            }
            if (
                session &&
                (event === 'PASSWORD_RECOVERY' ||
                    event === 'SIGNED_IN' ||
                    event === 'INITIAL_SESSION' ||
                    event === 'TOKEN_REFRESHED')
            ) {
                logger.info('Recovery/update-password: auth event with session', { event });
                markReady();
            }
        });

        (async () => {
            try {
                if (await tryPkceCode()) {
                    return;
                }
                if (await tryGetSession()) {
                    return;
                }

                for (let i = 0; i < 8 && !cancelled && !sessionReadyRef.current; i++) {
                    const delayMs = 250 * 2 ** i;
                    await new Promise((r) => setTimeout(r, delayMs));
                    if (await tryPkceCode()) {
                        return;
                    }
                    if (await tryGetSession()) {
                        return;
                    }
                }

                if (!cancelled && !sessionReadyRef.current) {
                    markError('No active session found. Please request a password reset.');
                }
            } catch (error) {
                logger.error('Error checking authentication status', error);
                if (!cancelled) {
                    markError('Failed to check authentication status. Please try again.');
                }
            }
        })();

        return () => {
            cancelled = true;
            subscription.unsubscribe();
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = (formData) => {
        const { password, confirmPassword } = formData;
        if (!password) {
            return 'Password is required';
        }
        if (password.length < 6) {
            return 'Password must be at least 6 characters';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        // No errors found
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setState((prevState) => ({ ...prevState, error: null, loading: true, message: null }));

        const errorMessage = validateForm(formData);
        if (errorMessage) {
            setState((prevState) => ({ ...prevState, error: errorMessage, loading: false }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            error: null,
            message: 'Password update initiated...',
        }));

        logger.info('Update password form submitted');
        try {
            await updatePassword(formData);
            setState((prevState) => ({
                ...prevState,
                error: null,
                loading: false,
                message: 'Password updated successfully!',
            }));

            // Give the user time to see the success message before redirecting
            setTimeout(() => {
                setState((prevState) => ({ ...prevState, loading: false }));
                navigate('/login');
            }, 2000);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            logger.error('Error in update password form submission', { error: errorMessage });
            setState((prevState) => ({
                ...prevState,
                error: errorMessage,
                loading: false,
                message: null,
            }));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg space-y-6"
            >
                <h2 className="text-2xl font-semibold text-center text-gray-800">
                    Update Your Password
                </h2>

                {state.error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {state.error}
                    </div>
                )}
                {state.message && !state.error && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                        {state.message}
                    </div>
                )}

                {/* New Password input field with toggle */}
                <div className="relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="New Password"
                        disabled={state.loading}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={state.loading}
                    >
                        {showPassword ? (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Confirm New Password input field with toggle */}
                <div className="relative">
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm New Password"
                        disabled={state.loading}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={state.loading}
                    >
                        {showConfirmPassword ? (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                <button
                    type="submit"
                    className="w-full bg-card text-white py-3 rounded-lg hover:bg-primary transition font-medium"
                    disabled={state.loading}
                >
                    {state.loading ? 'Updating Password...' : 'Update Password'}
                </button>

                <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition"
                    disabled={state.loading}
                >
                    Back to Login
                </button>
            </form>
        </div>
    );
}
